import React, { useEffect } from 'react';
import styled, { AnyStyledComponent } from 'styled-components';
import Dropdown, { Option } from '^/components/atoms/Dropdown/1';
import Button from '^/components/atoms/Button';
import theme from '^/theme';
import CloseSVG from '^/assets/icons/close-gray.svg';
import ErrorInfoIcon from '^/assets/icons/error-info.svg';
import InfoSvg from '^/assets/icons/share-banner/info.svg';
import { usePermissionDropdownOptions } from '^/components/molecules/ProjectPermissionItem';
import Tippy from '@tippyjs/react';
import * as T from '^/types';
import { usePopupStore } from '^/store/popup';
import { useL10n } from '^/hooks';
import Text from './text';
import PlusSvg from '^/assets/icons/plus.svg';
import { useGetPermissions } from '^/hooks/projects/useGetPermissions';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { State } from '^/types';

const Root = styled.div({
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
});

const Header = styled.div({
  gap: '8px',
  display: 'flex',
  alignItems: 'center',
});

const Title = styled.h2({
  fontSize: '18px',
  fontWeight: '600',
  color: theme.colorTokens.textCoolDark,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ViewDetails = styled.span<{ isPopup?: boolean }>(({ isPopup }) => ({
  color: theme.colorTokens.textCoolLight,
  fontSize: '14px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  padding: '4px',
  borderRadius: '8px',
  gap: '2px',
  '&:hover': {
    backgroundColor: theme.colorTokens.focusLightBlue,
  },
  ...(isPopup && {
    marginTop: '2px',
  }),
}));

const InputRow = styled.div<{ isPopup?: boolean }>(({ isPopup }) => ({
  display: 'flex',
  alignItems: 'self-start',
  gap: '12px',
  ...(isPopup && {
    marginTop: '20px',
  }),
}));

const RoleDropdown = styled(Dropdown as AnyStyledComponent)({
  width: '200px',
  minWidth: '200px',
  height: '48px',
});

const EmailInput = styled.input<{ hasError?: boolean }>(({ hasError }) => ({
  flex: 1,
  paddingLeft: '12px',
  border: `1px solid ${hasError ? theme.colorTokens.errorRed : theme.colorTokens.inputBorderColor}`,
  borderRadius: '8px',
  fontSize: '16px',
  height: '48px',
  backgroundColor: theme.colorTokens.bgLightCoolGray,
  '&::placeholder': {
    color: theme.colorTokens.textGrayLight,
  },
}));

const CollaboratorList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const CollaboratorRow = styled.div<{ hasError?: boolean; isDuplicate?: boolean }>(
  ({ hasError, isDuplicate }) => ({
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    gap: '12px',
    padding: '12px',
    color: hasError ? theme.colorTokens.errorRed : theme.colors.neutralGray[400],
    backgroundColor: hasError
      ? theme.colors.pointRed[50]
      : isDuplicate
      ? theme.colors.pointYellow[100]
      : theme.colorTokens.bgLightCoolGray,
    borderRadius: '4px',
  })
);

const CloseIcon = styled(CloseSVG)({
  '& path': {
    fill: theme.colorTokens.textCool,
  },
});

const HelperText = styled.p({
  color: theme.colorTokens.textCoolLight,
  fontSize: '14px',
  margin: '4px 0',
});

const FooterText = styled.p({
  color: theme.colorTokens.textCoolLight,
  fontSize: '14px',
  borderLeft: `1px solid ${theme.colorTokens.lineCoolLight}`,
  paddingLeft: '8px',
});

const Wrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const WhitePlusIcon = styled(PlusSvg)({
  width: '16px',
  '& path': {
    fill: '#fff',
  },
});

const InfoIcon = styled(InfoSvg)({
  width: '16px',
  '& path': {
    fill: theme.colorTokens.textCoolLight,
  },
});

const EmailWrapper = styled.span({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  paddingBottom: '2px',
});

const EmailInputWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flexGrow: 1,
});

const EmailInputRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  height: '49px',
});

const DropdownWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const CloseButton = styled.button({
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  padding: '4px',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: theme.colors.coolGray[50],
  },
  '&:active': {
    backgroundColor: theme.colorTokens.focusLightBlue,
  },
});

export interface Collaborator {
  role: string;
  email: string;
  isValid: boolean;
  isExisting?: boolean;
}

interface AddCollaboratorProps {
  onChange(values: Collaborator[]): void;
  isValid: boolean;
  values: Collaborator[];
  isLoading: boolean;
  isPopup?: boolean;
}

const AddCollaborator: React.FC<AddCollaboratorProps> = ({ onChange, values, isPopup = false }) => {
  const [l10n] = useL10n();
  const { id: projectId } = useParams();
  const [selectedRole, setSelectedRole] = React.useState('admin');
  const [emailInput, setEmailInput] = React.useState('');
  const [highlightedEmails, setHighlightedEmails] = React.useState<string[]>([]);
  const [collaborators, setCollaborators] = React.useState<Collaborator[]>(values);
  const { permissions } = useGetPermissions({
    projectId: projectId?.toString(),
  }) as { permissions: T.Permission[] | undefined };

  const setPopup = usePopupStore(s => s.setMinorPopup);
  const setPopupOptions = usePopupStore(s => s.setPopupOptions);
  const setThirdPopup = usePopupStore(s => s.setThirdPopup);

  const isInContent = useSelector((state: State) => !state.Pages.Project.editingProjectId);

  const roleOptions = usePermissionDropdownOptions();

  const isDuplicateEmail = (email: string, currentIndex?: number): boolean =>
    collaborators.some(
      (c: Collaborator, index: number) =>
        c.email?.toLowerCase() === email?.toLowerCase() &&
        (currentIndex === undefined || index !== currentIndex)
    ) || highlightedEmails.includes(email?.toLowerCase());

  const isExistingEmail = (email: string): boolean =>
    permissions?.some(
      (permission: T.Permission) => permission.email?.toLowerCase() === email?.toLowerCase()
    ) || false;

  const validateEmail = (email: string): { isValid: boolean; isExisting: boolean } => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = re.test(email?.trim());
    const isExisting = isExistingEmail(email);
    return { isValid, isExisting };
  };

  useEffect(() => {
    if (highlightedEmails.length > 0) {
      const timer = setTimeout(() => {
        setHighlightedEmails([]);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [highlightedEmails]);

  const handleAddCollaborators = () => {
    if (!emailInput.trim()) {
      return;
    }

    const emails = emailInput
      .split(',')
      .filter(email => email.trim())
      .map(email => email.trim());
    const uniqueEmails = new Set<string>();
    const duplicateEmails = new Set<string>();

    emails.forEach(email => {
      const lowerEmail = email.toLowerCase();
      if (
        uniqueEmails.has(lowerEmail) ||
        collaborators.some(c => c.email.toLowerCase() === lowerEmail)
      ) {
        duplicateEmails.add(lowerEmail);
      } else {
        uniqueEmails.add(lowerEmail);
      }
    });

    if (duplicateEmails.size > 0) {
      setHighlightedEmails([...duplicateEmails]);
    }

    const newCollaborators = [...uniqueEmails].map(email => {
      const { isValid, isExisting } = validateEmail(email);
      return {
        role: selectedRole,
        email,
        isValid,
        isExisting,
      };
    });

    if (newCollaborators.length > 0) {
      const updatedCollaborators = [...collaborators, ...newCollaborators];
      setCollaborators(updatedCollaborators);
      onChange(updatedCollaborators);
    }

    setEmailInput('');
  };

  const handleRemoveCollaborator = (index: number) => {
    const newCollaborators = collaborators.filter((_, i) => i !== index);
    setCollaborators(newCollaborators);
    onChange(newCollaborators);
  };

  const handleRoleChange = (option: Option) => {
    setSelectedRole(option.value as string);
  };

  const handleCollaboratorRoleChange = (index: number, option: Option) => {
    const newCollaborators = [...collaborators];
    newCollaborators[index].role = option.value as string;
    setCollaborators(newCollaborators);
  };

  const onClickHandler = (type: T.ProjectPagePopupType) => {
    setPopup(T.ContentPageMinorPopupType.PROJECT_PAGE_POPUP);
    setPopupOptions({ projectPagePopupType: type });
  };

  const onDetailsPopup = () => {
    if (isInContent) {
      setThirdPopup(T.ContentPageMinorPopupType.ROLE_INFORMATION_POPUP);
    } else {
      setPopup(T.ContentPageMinorPopupType.MANAGE_PROJECT_POPUP);
      setPopupOptions({ manageProjectPopupType: T.ManageProjectPopupType.VIEW_ROLE_INFORMATION });
    }
  };

  return (
    <Root>
      {!isPopup && (
        <Header>
          <Title>{l10n(Text.addCollaborators)}</Title>
          <ViewDetails
            data-testid="view-roles-detail-button"
            onClick={() => onClickHandler(T.ProjectPagePopupType.PROJECT_ROLE_INFORMATION)}
          >
            <InfoIcon />
            {l10n(Text.viewDetails)}
          </ViewDetails>
        </Header>
      )}
      <InputRow isPopup={isPopup}>
        <DropdownWrapper>
          <RoleDropdown
            zIndex={0}
            value={selectedRole}
            options={roleOptions}
            onClick={handleRoleChange}
            placeHolder={l10n(Text.selectRole)}
          />
          {isPopup && (
            <ViewDetails
              data-testid="detail-popup-button"
              isPopup={isPopup}
              onClick={onDetailsPopup}
            >
              <InfoIcon />
              {l10n(Text.viewDetails)}
            </ViewDetails>
          )}
        </DropdownWrapper>
        <EmailInputWrapper>
          <EmailInputRow>
            <EmailInput
              data-testid="add-collaborator-email-input"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder={l10n(Text.emailPlaceholder)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleAddCollaborators();
                }
              }}
            />
          </EmailInputRow>
          <HelperText>{l10n(Text.multipleEmailsHelper)}</HelperText>
        </EmailInputWrapper>
        <Button
          variant="primary"
          size="large"
          data-testid="add-collaborator-button"
          onClick={handleAddCollaborators}
          disabled={!emailInput.trim()}
        >
          <WhitePlusIcon />
          {l10n(Text.add)}
        </Button>
      </InputRow>
      {collaborators?.length === 0 && (
        <CollaboratorRow hasError={false} isDuplicate={false}>
          <ViewDetails>{l10n(Text.noCollaborators)}</ViewDetails>
        </CollaboratorRow>
      )}
      <CollaboratorList>
        {collaborators.map((collaborator, index) => (
          <Wrapper key={index}>
            <CollaboratorRow
              hasError={!collaborator.isValid || collaborator.isExisting}
              isDuplicate={isDuplicateEmail(collaborator.email, index)}
            >
              <RoleDropdown
                value={collaborator.role}
                options={roleOptions}
                onClick={(option: Option) => handleCollaboratorRoleChange(index, option)}
                placeHolder={l10n(Text.selectRole)}
                mainButtonStyle={{ backgroundColor: 'transparent', border: 0, boxShadow: 'none' }}
                zIndex={0}
              />
              <EmailWrapper>{collaborator.email}</EmailWrapper>
              {(!collaborator.isValid || collaborator.isExisting) && (
                <Tippy
                  offset={T.TIPPY_OFFSET}
                  theme="angelsw"
                  placement="top"
                  arrow={false}
                  content={l10n(
                    collaborator.isExisting ? Text.existingEmailError : Text.invalidEmailError
                  )}
                >
                  <span>
                    <ErrorInfoIcon />
                  </span>
                </Tippy>
              )}
            </CollaboratorRow>
            <CloseButton
              data-testid="remove-collaborator-button"
              onClick={() => handleRemoveCollaborator(index)}
            >
              <CloseIcon />
            </CloseButton>
          </Wrapper>
        ))}
        {!isPopup && <FooterText>{l10n(Text.infoText)}</FooterText>}
      </CollaboratorList>
    </Root>
  );
};

export default AddCollaborator;
